<template>
  <!-- START : Scan Overlay -->
  <div
    class="scan-overlay"
    :style="{ transform: dragCurrentHeight, transitionProperty: isDragging ? 'none' : '' }"
  >
    <!-- START : Drag Icon -->
    <button
      class="unstyled-btn drag-container w-100"
      @click="handleDragIcon()"
      :aria-label="$t('drag')"
    >
      <font-awesome-icon :icon="dragIcon" class="font-size-2xl text-muted" />
    </button>
    <!-- END : Drag Icon -->

    <!-- START : Close Icon -->
    <button
      class="unstyled-btn close-container"
      v-if="hasCancel"
      @click="$emit('close')"
      :aria-label="$t('close')"
    >
      <font-awesome-icon class="close-icon font-size-2xl text-muted" icon="xmark" />
    </button>
    <!-- END : Close Icon -->

    <!-- START : Slot Area -->
    <div class="slot-container" :style="{ height: slotContainerHeight }">
      <slot></slot>
    </div>
    <!-- END : Slot Area -->
  </div>
  <!-- END : Product Overlay -->
</template>
<script>
export default {
  props: {
    hasCancel: {
      type: Boolean,
      default: true,
    },
    minHeight: {
      type: Number,
      default: 60,
    },
    maxHeight: {
      type: Number,
      default: 25,
    },
    expandView: {
      type: Boolean,
    },
  },
  data() {
    return {
      showFullView: false,
      isDragging: false,
      dragMinHeight: 0,
      dragMaxHeight: 0,
      dragCurrentHeight: '',
      slotScrollableMinHeight: 0,
      slotScrollableMaxHeight: 0,
      slotContainerHeight: 'auto',
      dragElement: undefined,
      componentElement: undefined,
      dragIcon: 'angle-up',
    };
  },
  created() {
    window.addEventListener('resize', this.resize);
  },
  mounted() {
    // calculate default device width and height
    this.calculateHeight();

    // configuring elements and event listeners for the drag
    this.$nextTick(() => {
      this.componentElement = this.$el;
      this.dragElement = this.$el.getElementsByClassName('drag-container')[0];
      this.dragElement.addEventListener('touchstart', this.touchStartHandler);
    });
  },
  destroyed() {
    // Removing the start event listner
    this.dragElement.removeEventListener('touchstart', this.touchStartHandler);
    window.removeEventListener('resize', this.resize);
  },
  methods: {
    resize() {
      this.calculateHeight();
    },
    /**
     * Container min and max height calculation
     */
    calculateHeight() {
      const height = window.innerHeight;
      const minHeight = parseInt(height * (this.minHeight / 100), 10);
      const maxHeight = parseInt(height * (this.maxHeight / 100), 10);
      this.dragCurrentHeight = `translateY(${minHeight}px)`;
      this.dragMinHeight = minHeight;
      this.dragMaxHeight = maxHeight;

      this.slotScrollableMinHeight = parseInt(height * ((100 - this.minHeight) / 100), 10) - 25;
      this.slotScrollableMaxHeight = parseInt(height * ((100 - this.maxHeight) / 100), 10) - 25;
      this.slotContainerHeight = `${this.slotScrollableMinHeight}px`;
    },

    /**
     * Touch start handler - Initating the drag events
     */
    touchStartHandler() {
      this.isDragging = true;
      this.componentElement.addEventListener('touchmove', this.touchMoveHandler);
      this.componentElement.addEventListener('touchend', this.touchEndHandler);
      this.componentElement.addEventListener('touchcancel', this.touchEndHandler);
      this.dragElement.removeEventListener('touchstart', this.touchStartHandler);
    },

    /**
     * Touch end handler - Removing the drag events
     */
    touchEndHandler() {
      this.isDragging = false;
      this.componentElement.removeEventListener('touchmove', this.touchMoveHandler, false);
      this.componentElement.removeEventListener('touchend', this.touchEndHandler, false);
      this.componentElement.removeEventListener('touchcancel', this.touchEndHandler, false);
      this.dragElement.addEventListener('touchstart', this.touchStartHandler);
    },

    /**
     * Touch move handler - Rearrangling the container based on the drag height
     */
    touchMoveHandler(event) {
      event.preventDefault();
      if (event.type === 'touchmove') {
        const clientY = (event.touches && event.touches.length && event.touches[0].clientY) || 0;
        if (clientY > this.dragMinHeight) {
          this.assignMinHeight();
        } else if (clientY < this.dragMaxHeight) {
          this.assignMaxHeight();
        } else {
          this.assignDynamicHeight(clientY);
        }
      }
    },

    /**
     * Method to assign minumum draggable height
     */
    assignMinHeight() {
      this.dragIcon = 'angle-up';
      this.showFullView = false;
      this.dragCurrentHeight = `translateY(${this.dragMinHeight}px)`;
      this.slotContainerHeight = `${this.slotScrollableMinHeight}px`;
    },

    /**
     * Method to assign maximum draggable height
     */
    assignMaxHeight() {
      this.dragIcon = 'angle-down';
      this.showFullView = true;
      this.dragCurrentHeight = `translateY(${this.dragMaxHeight}px)`;
      this.slotContainerHeight = `${this.slotScrollableMaxHeight}px`;
    },

    /**
     * Method to assign maximum draggable height
     */
    assignDynamicHeight(height) {
      this.dragIcon = 'dash';
      this.dragCurrentHeight = `translateY(${height}px)`;

      const bottomHeight = window.innerHeight - height - 25;
      this.slotContainerHeight = `${bottomHeight}px`;
    },

    /**
     * Method to handle the drag icon
     */
    handleDragIcon() {
      if (this.showFullView) {
        this.assignMinHeight();
      } else {
        this.assignMaxHeight();
      }
    },
  },

  watch: {
    expandView() {
      if (this.expandView) this.assignMaxHeight();
      else this.assignMinHeight();
    },
  },
};
</script>
<style lang="scss" scoped>
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

/**
  *  Overlay to show the product details
  */
.scan-overlay {
  position: fixed;
  width: 100%;
  left: 0;
  top: 0;
  background-color: #ffffff;
  border-radius: 1.25rem 1.25rem 0 0;
  z-index: 99;
  -moz-transition-duration: 0.8s;
  -webkit-transition-duration: 0.8s;
  -o-transition-duration: 0.8s;
  transition-duration: 0.8s;
  -moz-transition-timing-function: ease-in;
  -webkit-transition-timing-function: ease-in;
  -o-transition-timing-function: ease-in;
  transition-timing-function: ease-in;
}

.drag-container {
  padding: 5px;
}

.close-container {
  position: absolute;
  top: 0;
  right: 0.25rem;
}

.close-icon {
  margin-top: 0.75rem;
  margin-right: 0.75rem;
}

.slot-container {
  overflow-y: scroll;
}
</style>
